"use client";
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// Chocolate theme — warm dark gradient
const CHOC_BG: React.CSSProperties = {
  background:
    "linear-gradient(135deg, #1a0e07 0%, #2d1810 35%, #4a2c1a 100%)",
};

const FONT = "Inter, system-ui, sans-serif";

// Stronger drop shadow on overlay text so it stays readable on any base video
const TEXT_SHADOW =
  "0 4px 18px rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.9)";

// ─────────────────────────────────────────────────────────
// 1. Moisture Comparison: 60% → 7%
// ─────────────────────────────────────────────────────────
const MoistureCompare: React.FC = () => {
  const frame = useCurrentFrame();
  const beforeVal = Math.round(interpolate(frame, [8, 36], [0, 60], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const afterVal = Math.round(interpolate(frame, [22, 50], [0, 7], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const maxH = 280;
  const beforeH = interpolate(frame, [8, 36], [0, (60 / 100) * maxH], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const afterH = interpolate(frame, [22, 50], [0, (7 / 100) * maxH], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const labelOp = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", top: 80, color: "#f5e8d3", fontSize: 26, fontWeight: 600, letterSpacing: "0.15em", textShadow: TEXT_SHADOW, opacity: labelOp }}>
        STEP 03 — DRYING
      </div>
      <div style={{ position: "absolute", top: 120, color: "#d4a574", fontSize: 38, fontWeight: 800, textShadow: TEXT_SHADOW, opacity: labelOp }}>
        MOISTURE LOSS
      </div>

      <div style={{ display: "flex", gap: 220, alignItems: "flex-end", marginTop: 80 }}>
        {/* BEFORE */}
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#fff", fontSize: 96, fontWeight: 800, textShadow: TEXT_SHADOW, lineHeight: 1, marginBottom: 12 }}>
            {beforeVal}<span style={{ fontSize: 52 }}>%</span>
          </div>
          <div style={{ width: 140, height: beforeH, background: "linear-gradient(180deg, #c69a73 0%, #8b6647 100%)", margin: "0 auto", borderRadius: 4, boxShadow: "0 8px 30px rgba(0,0,0,0.6)" }} />
          <div style={{ color: "#f5e8d3", fontSize: 22, marginTop: 16, letterSpacing: "0.1em", textShadow: TEXT_SHADOW }}>RAW BEAN</div>
        </div>

        {/* arrow */}
        <div style={{ alignSelf: "center", color: "#d4a574", fontSize: 60, textShadow: TEXT_SHADOW, opacity: interpolate(frame, [18, 30], [0, 1], { extrapolateRight: "clamp" }) }}>→</div>

        {/* AFTER */}
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#fff", fontSize: 96, fontWeight: 800, textShadow: TEXT_SHADOW, lineHeight: 1, marginBottom: 12 }}>
            {afterVal}<span style={{ fontSize: 52 }}>%</span>
          </div>
          <div style={{ width: 140, height: afterH, background: "linear-gradient(180deg, #d4a574 0%, #b88a4f 100%)", margin: "0 auto", borderRadius: 4, boxShadow: "0 8px 30px rgba(0,0,0,0.6)" }} />
          <div style={{ color: "#f5e8d3", fontSize: 22, marginTop: 16, letterSpacing: "0.1em", textShadow: TEXT_SHADOW }}>DRIED BEAN</div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 60, color: "#a0825d", fontSize: 22, letterSpacing: "0.1em", textShadow: TEXT_SHADOW, opacity: interpolate(frame, [40, 56], [0, 1], { extrapolateRight: "clamp" }) }}>
        7 DAYS IN OPEN SUN
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// 2. Stat: $31.87 BILLION industry
// ─────────────────────────────────────────────────────────
const StatBillion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scaleS = spring({ frame, fps, config: { damping: 14, stiffness: 90 } });
  const count = interpolate(frame, [12, 60], [0, 31.87], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const labelOp = interpolate(frame, [40, 56], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#d4a574", fontSize: 28, letterSpacing: "0.25em", marginBottom: 24, textShadow: TEXT_SHADOW }}>
        GLOBAL CHOCOLATE INDUSTRY
      </div>
      <div style={{ transform: `scale(${scaleS})`, display: "flex", alignItems: "baseline", gap: 12 }}>
        <span style={{ color: "#fff", fontSize: 60, fontWeight: 800, textShadow: TEXT_SHADOW }}>$</span>
        <span style={{ color: "#fff", fontSize: 200, fontWeight: 800, letterSpacing: "-4px", textShadow: TEXT_SHADOW, lineHeight: 1 }}>
          {count.toFixed(2)}
        </span>
        <span style={{ color: "#d4a574", fontSize: 64, fontWeight: 700, textShadow: TEXT_SHADOW }}>B</span>
      </div>
      <div style={{ color: "#f5e8d3", fontSize: 24, letterSpacing: "0.18em", marginTop: 28, textShadow: TEXT_SHADOW, opacity: labelOp }}>
        ANNUAL · 2024
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// 3. Lower-third: RODOLPHE LINDT · 1879 · SWITZERLAND
// ─────────────────────────────────────────────────────────
const LowerThirdLindt: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const slideX = spring({ frame, fps, from: -500, to: 0, durationInFrames: 25, config: { damping: 16 } });
  const textOp = spring({ frame: Math.max(0, frame - 12), fps, from: 0, to: 1, durationInFrames: 18 });
  const accentW = interpolate(frame, [0, 30], [0, 280], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={CHOC_BG}>
      <div style={{ position: "absolute", bottom: 110, left: 80, transform: `translateX(${slideX}px)`, fontFamily: FONT }}>
        <div style={{ height: 3, width: accentW, background: "linear-gradient(90deg, #d4a574, #b88a4f)", marginBottom: 14, boxShadow: "0 0 12px rgba(212,165,116,0.5)" }} />
        <div style={{ color: "#fff", fontSize: 56, fontWeight: 800, letterSpacing: "-1px", textShadow: TEXT_SHADOW, opacity: textOp, lineHeight: 1.05 }}>
          RODOLPHE LINDT
        </div>
        <div style={{ color: "#d4a574", fontSize: 26, fontWeight: 500, letterSpacing: "0.2em", marginTop: 10, textShadow: TEXT_SHADOW, opacity: textOp }}>
          SWITZERLAND · 1879
        </div>
        <div style={{ color: "#a0825d", fontSize: 22, fontStyle: "italic", marginTop: 6, textShadow: TEXT_SHADOW, opacity: textOp }}>
          invented conching by accident
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// 4. Chapter Title: STEP 04 — ROASTING
// ─────────────────────────────────────────────────────────
const ChapterStep04: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const numS = spring({ frame, fps, config: { damping: 12, stiffness: 80 } });
  const subOp = interpolate(frame, [22, 42], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const subY = interpolate(frame, [22, 42], [22, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const lineW = interpolate(frame, [12, 42], [0, 160], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const labelOp = interpolate(frame, [6, 22], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
      <p style={{ color: "#a0825d", fontSize: 22, fontWeight: 500, letterSpacing: "0.3em", textTransform: "uppercase", margin: 0, marginBottom: 14, opacity: labelOp, textShadow: TEXT_SHADOW }}>
        Step
      </p>
      <h1 style={{ color: "#fff", fontSize: "12rem", fontWeight: 800, margin: 0, lineHeight: 1, transform: `scale(${numS})`, textShadow: TEXT_SHADOW }}>
        04
      </h1>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 30, marginBottom: 18 }}>
        <div style={{ height: 1, width: lineW, backgroundColor: "#d4a574" }} />
        <div style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#d4a574", opacity: labelOp, boxShadow: "0 0 10px rgba(212,165,116,0.7)" }} />
        <div style={{ height: 1, width: lineW, backgroundColor: "#d4a574" }} />
      </div>
      <h2 style={{ color: "#f5e8d3", fontSize: "3.2rem", fontWeight: 800, letterSpacing: "0.05em", margin: 0, opacity: subOp, transform: `translateY(${subY}px)`, textShadow: TEXT_SHADOW }}>
        ROASTING
      </h2>
      <p style={{ color: "#d4a574", fontSize: "1.4rem", marginTop: 14, letterSpacing: "0.15em", opacity: subOp, textShadow: TEXT_SHADOW }}>
        140°C · 20–40 MIN
      </p>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// 5. Donut: Dark vs Milk cocoa solids
// ─────────────────────────────────────────────────────────
const DonutDarkMilk: React.FC = () => {
  const frame = useCurrentFrame();
  const cx = 640, cy = 360, r = 130, sw = 28;
  const circ = 2 * Math.PI * r;
  const darkPct = 70, milkPct = 25;
  const darkDraw = interpolate(frame, [10, 50], [0, (darkPct / 100) * circ], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const milkDraw = interpolate(frame, [25, 65], [0, (milkPct / 100) * circ], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleOp = interpolate(frame, [0, 14], [0, 1], { extrapolateRight: "clamp" });
  const labelOp = interpolate(frame, [40, 60], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", top: 60, color: "#f5e8d3", fontSize: 30, fontWeight: 700, letterSpacing: "0.2em", textShadow: TEXT_SHADOW, opacity: titleOp }}>
        COCOA SOLIDS · TYPICAL
      </div>
      <svg width={1280} height={620} style={{ overflow: "visible" }}>
        <circle cx={cx - 200} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} />
        <circle
          cx={cx - 200} cy={cy} r={r} fill="none"
          stroke="#4a2c1a" strokeWidth={sw} strokeLinecap="round"
          strokeDasharray={`${darkDraw} ${circ}`}
          transform={`rotate(-90 ${cx - 200} ${cy})`}
        />
        <text x={cx - 200} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="76" fontWeight="800" style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.6))" }}>{darkPct}%</text>

        <circle cx={cx + 200} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} />
        <circle
          cx={cx + 200} cy={cy} r={r} fill="none"
          stroke="#c69a73" strokeWidth={sw} strokeLinecap="round"
          strokeDasharray={`${milkDraw} ${circ}`}
          transform={`rotate(-90 ${cx + 200} ${cy})`}
        />
        <text x={cx + 200} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="76" fontWeight="800" style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.6))" }}>{milkPct}%</text>
      </svg>
      <div style={{ position: "absolute", bottom: 100, display: "flex", gap: 280, opacity: labelOp }}>
        <div style={{ color: "#f5e8d3", fontSize: 28, fontWeight: 700, letterSpacing: "0.18em", textShadow: TEXT_SHADOW }}>DARK</div>
        <div style={{ color: "#f5e8d3", fontSize: 28, fontWeight: 700, letterSpacing: "0.18em", textShadow: TEXT_SHADOW }}>MILK</div>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// 6. Line chart: Tempering curve 45 → 27 → 31 °C
// ─────────────────────────────────────────────────────────
const TempCurve: React.FC = () => {
  const frame = useCurrentFrame();
  const data = [
    { x: 0,  y: 45, label: "INIT" },
    { x: 1,  y: 45, label: "" },
    { x: 2,  y: 42, label: "" },
    { x: 3,  y: 36, label: "" },
    { x: 4,  y: 30, label: "" },
    { x: 5,  y: 27, label: "SEED" },
    { x: 6,  y: 27, label: "" },
    { x: 7,  y: 29, label: "" },
    { x: 8,  y: 31, label: "HOLD" },
    { x: 9,  y: 31, label: "" },
  ];
  const cW = 1000, cH = 460, pad = 80;
  const minY = 20, maxY = 50;
  const xs = (x: number) => (x / (data.length - 1)) * (cW - pad * 2) + pad;
  const ys = (y: number) => cH - pad - ((y - minY) / (maxY - minY)) * (cH - pad * 2);

  const points = data.map(d => `${xs(d.x)},${ys(d.y)}`).join(" ");
  let total = 0;
  for (let i = 1; i < data.length; i++) {
    const dx = xs(data[i].x) - xs(data[i - 1].x);
    const dy = ys(data[i].y) - ys(data[i - 1].y);
    total += Math.sqrt(dx * dx + dy * dy);
  }
  const dash = interpolate(frame, [10, 70], [total, 0], { extrapolateRight: "clamp" });
  const titleOp = interpolate(frame, [0, 14], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", top: 70, color: "#f5e8d3", fontSize: 28, fontWeight: 700, letterSpacing: "0.2em", textShadow: TEXT_SHADOW, opacity: titleOp }}>
        TEMPERING · FORM V CRYSTAL
      </div>
      <div style={{ position: "absolute", top: 105, color: "#d4a574", fontSize: 22, letterSpacing: "0.18em", textShadow: TEXT_SHADOW, opacity: titleOp }}>
        45° → 27° → 31°C
      </div>
      <svg width={cW} height={cH} style={{ marginTop: 60, overflow: "visible" }}>
        {[20, 30, 40, 50].map(v => (
          <g key={v}>
            <line x1={pad} y1={ys(v)} x2={cW - pad} y2={ys(v)} stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
            <text x={pad - 12} y={ys(v) + 6} textAnchor="end" fill="#a0825d" fontSize="16" fontFamily={FONT}>{v}°</text>
          </g>
        ))}
        <polyline
          points={points}
          fill="none"
          stroke="#d4a574"
          strokeWidth={4}
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray={total}
          strokeDashoffset={dash}
          style={{ filter: "drop-shadow(0 0 10px rgba(212,165,116,0.6))" }}
        />
        {data.filter(d => d.label).map((d, i) => {
          const showFrame = 14 + i * 8;
          const op = interpolate(frame, [showFrame, showFrame + 8], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <g key={i} opacity={op}>
              <circle cx={xs(d.x)} cy={ys(d.y)} r={7} fill="#fff" stroke="#d4a574" strokeWidth={3} />
              <text x={xs(d.x)} y={ys(d.y) - 18} textAnchor="middle" fill="#f5e8d3" fontSize="18" fontWeight="700" letterSpacing="0.1em" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.8))" }}>{d.label}</text>
              <text x={xs(d.x)} y={ys(d.y) + 30} textAnchor="middle" fill="#fff" fontSize="20" fontWeight="800" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.8))" }}>{d.y}°</text>
            </g>
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// Gap: gradient + small label of what comes next
// ─────────────────────────────────────────────────────────
const Gap: React.FC<{ nextLabel: string }> = ({ nextLabel }) => {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 8, 40, 48], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ ...CHOC_BG, fontFamily: FONT, alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#6f4a31", fontSize: 18, letterSpacing: "0.3em", textShadow: TEXT_SHADOW, opacity: op }}>
        NEXT
      </div>
      <div style={{ color: "#a0825d", fontSize: 30, fontWeight: 600, letterSpacing: "0.15em", marginTop: 14, textShadow: TEXT_SHADOW, opacity: op }}>
        {nextLabel}
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────
// Master timeline — 6 overlays × 5s + 5 gaps × 3s = 45s
// ─────────────────────────────────────────────────────────
//  @16fps:  5s = 80f,  3s = 48f
//  cumulative starts: 0, 80, 128, 208, 256, 336, 384, 464, 512, 592, 640, 720
//                     M  G   S    G   L    G   C    G   D    G   T
//  total: 720 frames = 45s
export const ChocolatePreview: React.FC = () => {
  return (
    <AbsoluteFill style={CHOC_BG}>
      <Sequence from={0}   durationInFrames={80} layout="none"><MoistureCompare /></Sequence>
      <Sequence from={80}  durationInFrames={48} layout="none"><Gap nextLabel="$31.87 BILLION INDUSTRY" /></Sequence>
      <Sequence from={128} durationInFrames={80} layout="none"><StatBillion /></Sequence>
      <Sequence from={208} durationInFrames={48} layout="none"><Gap nextLabel="LOWER-THIRD · RODOLPHE LINDT" /></Sequence>
      <Sequence from={256} durationInFrames={80} layout="none"><LowerThirdLindt /></Sequence>
      <Sequence from={336} durationInFrames={48} layout="none"><Gap nextLabel="CHAPTER · STEP 04 ROASTING" /></Sequence>
      <Sequence from={384} durationInFrames={80} layout="none"><ChapterStep04 /></Sequence>
      <Sequence from={464} durationInFrames={48} layout="none"><Gap nextLabel="DONUT · DARK vs MILK" /></Sequence>
      <Sequence from={512} durationInFrames={80} layout="none"><DonutDarkMilk /></Sequence>
      <Sequence from={592} durationInFrames={48} layout="none"><Gap nextLabel="LINE CHART · TEMPERING CURVE" /></Sequence>
      <Sequence from={640} durationInFrames={80} layout="none"><TempCurve /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
